import { IconeTrait } from '@/components/ui/icone-trait';
import { cn } from '@/lib/utils';
import { Icon, LoadingRing } from '@pms-digital/design-system';
import type { EtapeTraduction } from '@rosetta/shared';
import { useId } from 'react';
import { formaterDuree } from './formater-duree';

interface EtapesAttenteProps {
  /** The steps Tahiti lets the client read; `undefined` when it refuses (the box then only says "en cours"). */
  etapes: readonly EtapeTraduction[] | undefined;
}

function Marque({ etat }: { etat: EtapeTraduction['etat'] }) {
  return (
    <span aria-hidden="true" className="inline-flex w-[22px] justify-center">
      {etat === 'terminee' ? (
        <IconeTrait name="check_circle" size={22} className="text-bleu-nuit" />
      ) : etat === 'en_cours' ? (
        <span className="inline-flex text-bleu-nuit">
          <LoadingRing />
        </span>
      ) : etat === 'echec' ? (
        <span className="inline-flex shrink-0 text-rouge-deep">
          <Icon name="error" size={24} />
        </span>
      ) : (
        <IconeTrait name="radio_button_unchecked" size={22} className="text-gray-500" />
      )}
    </span>
  );
}

function motEtat(etape: EtapeTraduction): string {
  if (etape.etat === 'attente') return 'en attente';
  if (etape.etat === 'en_cours') return 'en cours';
  if (etape.etat === 'echec') return "n'a pas abouti";
  return etape.dureeSecondes === undefined
    ? 'terminée'
    : `terminée en ${formaterDuree(etape.dureeSecondes)}`;
}

/** What the box shows when Tahiti does not let the steps be read (`TO VALIDATE(#7)`). */
const REPLI: readonly EtapeTraduction[] = [
  { code: 'traduction', libelle: 'Traduction', etat: 'en_cours' },
];

/**
 * The "Étapes" box of the waiting screen W1 (#7): one line per step, in the order Rosetta v5
 * chains them, each with its state, "terminée en <durée>" (`check_circle`, bleu nuit), "en
 * cours" (spinner, label in Semibold), "en attente" (empty circle, grey) or, on a blocked
 * translation (#13), "n'a pas abouti" (`error`, `rouge-deep`, label and state in Semibold). The
 * running step, or the failed one, carries `aria-current="step"`. When Tahiti does not let the
 * steps be read, the box is one line, "en cours" (`TO VALIDATE(#7)`).
 */
export function EtapesAttente({ etapes }: EtapesAttenteProps) {
  const titreId = useId();

  return (
    <section aria-labelledby={titreId} className="mt-6 rounded-md border border-gray-200">
      <h2 id={titreId} className="px-6 py-4 font-bold text-body leading-[1.4]">
        Étapes
      </h2>
      <ol>
        {(etapes ?? REPLI).map((etape) => (
          <li
            key={`${etape.code}-${etape.noyau ?? ''}`}
            aria-current={etape.etat === 'en_cours' || etape.etat === 'echec' ? 'step' : undefined}
            className="flex items-center gap-4 border-gray-100 border-t px-6 py-4"
          >
            <Marque etat={etape.etat} />
            <span
              className={cn(
                'grow text-body leading-[1.4]',
                (etape.etat === 'en_cours' || etape.etat === 'echec') && 'font-semibold',
                etape.etat === 'attente' ? 'text-gray-600' : 'text-black',
              )}
            >
              {etape.libelle}
            </span>
            <span
              className={cn(
                'text-right text-small leading-[1.4]',
                etape.etat === 'echec' ? 'font-semibold text-rouge-deep' : 'text-gray-600',
              )}
            >
              {motEtat(etape)}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
